if (!customElements.get('sticky-atc-component')) {  
  class StickyAtcComponent extends HTMLElement {
    constructor() {
      super();
      this.isMobile = window.innerWidth <= 1024;
      this.backdrop = null;
      this.isOpen = false;
      this.footerObserver = null;
      this.mainContentObserver = null;

      // Bind methods to maintain context
      this.handleClose = this.handleClose.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleFooterVisibility = this.handleFooterVisibility.bind(this);
      this.handleMainContentVisibility = this.handleMainContentVisibility.bind(this);
      this.handleSwatchLabelKeydown = this.handleSwatchLabelKeydown.bind(this);

    }

    connectedCallback() {
      this.init();
      window.addEventListener('resize', this.handleResize);
      setTimeout(() => {
        this.setupFooterObserver();
        this.setupMainContentObserver();
      }, 500);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
      this.cleanup();
    }

    init() {
      this.closeElement = this.querySelector('.close-container');
      this.contentContainer = this.querySelector('.content-container');
      this.atcLinks = this.querySelectorAll('.atc-content a');
      this.variantInputs = this.querySelectorAll('.swatches-wrapper input[type="radio"]');
      this.swatchLabels = this.querySelectorAll(
        '.swatches-wrapper label.variant-swatch:not(.variant-swatch--unavailable)'
      );
      this.variantImages = this.querySelectorAll('.product-image [data-variant-id]');
      this.atcContainers = this.querySelectorAll('.atc-content > div[data-variant-id]');

      this.setupInitialState();
      this.createBackdrop();
      this.attachEventListeners();
    }

    handleFooterVisibility(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          this.style.display = 'none';
        } else {
          this.style.display = '';
        }
      });
    }

    setupFooterObserver() {
      const footer = document.querySelector('footer');
      if (footer) {
        this.footerObserver = new IntersectionObserver(this.handleFooterVisibility, {
          threshold: 0.5,
          rootMargin: '0px'
        });
        this.footerObserver.observe(footer);

        requestAnimationFrame(() => {
          const entries = [{ 
            target: footer, 
            isIntersecting: footer.getBoundingClientRect().top < window.innerHeight,
            intersectionRatio: 0.6
          }];
          this.handleFooterVisibility(entries);
        });
      }
    }

    setupInitialState() {
      if (this.isMobile && this.closeElement) {
        this.closeElement.style.display = 'none';
      }
      this.updateLinksText('ORDER NOW');
    }

    createBackdrop() {
      if (!this.isMobile) return;

      this.backdrop = document.createElement('div');
      Object.assign(this.backdrop.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.5)',
        zIndex: '9',
        display: 'none'
      });

      document.body.appendChild(this.backdrop);
      this.backdrop.addEventListener('click', this.handleClose);
    }

    attachEventListeners() {
      if (this.isMobile) {
        this.atcLinks.forEach((link, i) => {
          link.addEventListener('click', this.handleClick);
        });

        if (this.closeElement) {
          this.closeElement.addEventListener('click', this.handleClose);
        }
      }

      this.variantInputs.forEach((input, index) => {
        input.addEventListener('change', this.handleVariantChange);
      });

      this.swatchLabels.forEach((label) => {
        label.addEventListener('keydown', this.handleSwatchLabelKeydown);
      });
    }

    removeEventListeners() {
      this.atcLinks.forEach(link => {
        link.removeEventListener('click', this.handleClick);
      });

      if (this.closeElement) {
        this.closeElement.removeEventListener('click', this.handleClose);
      }

      this.variantInputs.forEach(input => {
        input.removeEventListener('change', this.handleVariantChange);
      });

      this.swatchLabels.forEach((label) => {
        label.removeEventListener('keydown', this.handleSwatchLabelKeydown);
      });

      if (this.backdrop) {
        this.backdrop.removeEventListener('click', this.handleClose);
      }
    }

    handleVariantChange(e) {
      const selectedVariantId = e.target.dataset.variantId;
      if (selectedVariantId) {
        this.updateVariantDisplay(selectedVariantId);
      }
    }

    handleSwatchLabelKeydown(e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const label = e.currentTarget;
      const forId = label.getAttribute('for');
      if (!forId) return;
      const input = this.querySelector('#' + (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(forId) : forId));
      if (!input || input.disabled) return;
      e.preventDefault();
      if (!input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateVariantDisplay(variantId) {
      this.variantImages.forEach((image, i) => {
        if (image.dataset.variantId) {
          image.style.display = 'none';
        }
      });

      this.atcContainers.forEach((container, i) => {
        container.style.display = 'none';
      });

      const selectedImage = this.querySelector(`.product-image [data-variant-id="${variantId}"]`);
      const selectedAtcContainer = this.querySelector(`.atc-content > div[data-variant-id="${variantId}"]`);

      if (selectedImage) {
        selectedImage.style.display = 'block';
      }

      if (selectedAtcContainer) {
        selectedAtcContainer.style.display = 'block';
      }

      this.atcLinks = this.querySelectorAll('.atc-content a');

      this.dispatchEvent(new CustomEvent('variant:changed', {
        bubbles: true,
        detail: { variantId, component: this }
      }));
    }

    handleClick(e) {
      if (!this.isMobile) return;

      e.preventDefault();
      e.stopPropagation();
      this.openModal();
    }

    handleClose() {
      this.closeModal();
    }

    openModal() {
      if (!this.isMobile || this.isOpen) return;

      this.isOpen = true;
      this.closeElement.style.display = 'block';
      this.contentContainer.style.display = 'flex';
      if (this.backdrop) this.backdrop.style.display = 'block';
      this.disableScroll();
      this.updateLinksText('ADD TO CART');

      this.atcLinks.forEach(link => {
        link.removeEventListener('click', this.handleClick);
      });

      this.dispatchEvent(new CustomEvent('modal:opened', {
        bubbles: true,
        detail: { component: this }
      }));
    }

    closeModal() {
      if (!this.isMobile || !this.isOpen) return;

      this.isOpen = false;
      this.contentContainer.style.display = 'none';
      this.closeElement.style.display = 'none';
      if (this.backdrop) this.backdrop.style.display = 'none';
      this.enableScroll();
      this.updateLinksText('ORDER NOW');

      this.atcLinks.forEach((link, i) => {
        link.addEventListener('click', this.handleClick);
      });

      this.dispatchEvent(new CustomEvent('modal:closed', {
        bubbles: true,
        detail: { component: this }
      }));
    }

    updateLinksText(text) {
      const visibleLinks = this.querySelectorAll('.atc-content .btn-atc-sticky span');
      visibleLinks.forEach(span => {
        span.textContent = text;
      });
    }

    disableScroll() {
      document.body.style.overflow = 'hidden';
    }

    enableScroll() {
      document.body.style.overflow = '';
    }

    setupMainContentObserver() {
      if (this.isMobile) return;

      const mainElement = document.querySelector('main');
      if (!mainElement) return;

      const secondChild = mainElement.children[1];
      if (!secondChild) return;

      this.mainContentObserver = new IntersectionObserver(this.handleMainContentVisibility, {
        threshold: 1.0,
        rootMargin: '0px 0px 0px 0px'
      });

      this.mainContentObserver.observe(secondChild);

      requestAnimationFrame(() => {
        const rect = secondChild.getBoundingClientRect();
        const isFullyVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

        if (isFullyVisible) {
          this.style.display = '';
        } else {
          this.style.display = 'none';
        }
      });
    }


    handleMainContentVisibility(entries) {
      if (this.isMobile) return;

      entries.forEach(entry => {
        const rect = entry.target.getBoundingClientRect();

        if (entry.isIntersecting && entry.intersectionRatio >= 1.0) {
          this.style.display = '';
        } 
        else if (rect.top > 0) {
          this.style.display = 'none';
        }
      });
    }

    handleResize() {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 1024;

      if (wasMobile && !this.isMobile) {
        this.cleanup();
        setTimeout(() => {
          this.setupFooterObserver();
          this.setupMainContentObserver();
        }, 100);
      }
      else if (!wasMobile && this.isMobile) {
        this.init();
      }
    }

    cleanup() {
      if (this.isOpen) {
        this.closeModal();
      }

      this.removeEventListeners();

      if (this.backdrop && this.backdrop.parentNode) {
        this.backdrop.parentNode.removeChild(this.backdrop);
        this.backdrop = null;
      }

      if (this.footerObserver) {
        this.footerObserver.disconnect();
        this.footerObserver = null;
      }

      if (this.mainContentObserver) {
        this.mainContentObserver.disconnect();
        this.mainContentObserver = null;
      }
    }

    open() {
      if (this.isMobile) {
        this.openModal();
      }
    }

    close() {
      if (this.isMobile) {
        this.closeModal();
      }
    }

    toggle() {
      if (this.isMobile) {
        this.isOpen ? this.closeModal() : this.openModal();
      }
    }

    getSelectedVariant() {
      const checkedInput = this.querySelector('.swatches-wrapper input[type="radio"]:checked');
      return checkedInput ? checkedInput.dataset.variantId : null;
    }

    setVariant(variantId) {
      const targetInput = this.querySelector(`input[data-variant-id="${variantId}"]`);
      if (targetInput) {
        targetInput.checked = true;
        this.updateVariantDisplay(variantId);
      }
    }
  }

  customElements.define('sticky-atc-component', StickyAtcComponent);
}